import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import React, { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import {
  FormikAddressField,
  FormikRadioGroup,
  FormikSelect,
  FormikTextField,
} from "../../../../../../../../components/FormikFields";
import FormikDatePicker from "../../../../../../../../components/FormikFields/FormikDatePicker";
import provincias from "../../../../../../../../util/defaults/provincias";
import { cityOptionsForPostalCode } from "../../../../../../../../util/geo/cityOptionsForPostalCode";
import FormBox from "../../../FormComponents/FormBox";
import FormBoxTitle from "../../../FormComponents/FormBoxTitle";
import useAddressGeocoder, { TAddress } from "../../hooks/useAddressGeocoder";
import { TSelectOption, TServiceValuationForm } from "../../types";
import AddressMap from "../AddressMap/AddressMap";

const StyledAddressWrapper = styled.div`
  margin-top: 1.5rem;
`;

interface ICuideoAsistFormProps {
  areaOptions: { value: string; label: string }[];
  regionOptions: { value: string; label: string }[];
}

const CuideoAsistForm = (props: ICuideoAsistFormProps) => {
  const { areaOptions, regionOptions } = props;
  const formikContext = useFormikContext<TServiceValuationForm>();
  const { formatMessage } = useIntl();
  const { geocodeAddress } = useAddressGeocoder();
  const [cityOptions, setCityOptions] = useState<TSelectOption[]>([]);

  const handleAddressChange = (address: string) => {};

  const addressSelectedCallback = useCallback(
    async (address: TAddress) => {
      let addr1 = address.route ?? "";
      if (address.street_number) {
        addr1 += `, ${address.street_number}`;
      }
      const region = regionOptions.find(
        (region) => region.value === address.region
      );
      const area = areaOptions.find((area) => area.value === address.area);

      if (address.postal_code) {
        const newCityOptions = await cityOptionsForPostalCode(
          address.postal_code
        );
        if (newCityOptions.length === 0) {
          if (address.locality) {
            newCityOptions.push({
              value: address.locality,
              label: address.locality,
            });
          }
        } else {
          if (newCityOptions[0]) {
            address.locality = newCityOptions[0].value;
          }
        }
        setCityOptions(newCityOptions);
      } else {
        setCityOptions([]);
      }

      formikContext.setValues({
        ...formikContext.values,
        Direccion_entrega_Cuideo_Asist: addr1,
        Ciudad_entrega_Cuideo_Asist: address.locality
          ? {
              value: address.locality,
              label: address.locality,
            }
          : null,
        Region_entrega_Cuideo_Asist: region ?? null,
        Area_entrega_Cuideo_Asist: area ?? null,
        Codigo_postal_entrega_Cuideo_Asist: address.postal_code ?? "",
        Latitud_entrega_Cuideo_Asist: address.lat,
        Longitud_entrega_Cuideo_Asist: address.lng,
        formatted_address_entrega_Cuideo_Asist: address.formatted_address ?? "",
      });
    },
    [areaOptions, formikContext, regionOptions]
  );

  const handleAddressSelect = (address: string) => {
    geocodeAddress(address, addressSelectedCallback);
  };

  const handlePostalCodeChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.target.value.replace(/\D/g, "");
    if (e.target.value.length > 5) {
      e.target.value = e.target.value.substring(0, 5);
    }
    const postalCode = e.target.value;

    if (postalCode.length === 5) {
      const code = postalCode.substring(0, 2);
      const prov = provincias.find((item: any) => {
        return item.code === code && item.businessCountry === "es";
      });
      if (prov) {
        const selectedRegion = regionOptions.find(
          (region) => region.value === prov.value
        );
        if (selectedRegion) {
          formikContext.setFieldValue(
            "Region_entrega_Cuideo_Asist",
            selectedRegion
          );
        }

        if (prov.parentArea) {
          const selectedArea = areaOptions.find(
            (area) => area.value === prov.parentArea
          );
          if (selectedArea) {
            formikContext.setFieldValue(
              "Area_entrega_Cuideo_Asist",
              selectedArea
            );
          }
        }
      }

      const newCityOptions = await cityOptionsForPostalCode(postalCode);
      setCityOptions(newCityOptions);
      if (newCityOptions.length === 0) {
        formikContext.setFieldValue("Ciudad_entrega_Cuideo_Asist", null);
      }
      if (newCityOptions[0]) {
        formikContext.setFieldValue(
          "Ciudad_entrega_Cuideo_Asist",
          newCityOptions[0]
        );
      }
      formikContext.setFieldTouched("Ciudad_entrega_Cuideo_Asist");
    }
  };

  return (
    <FormBox>
      <FormBoxTitle
        title={formatMessage({
          id: "CuideoAsistForm.CuideoAsist",
          defaultMessage: "Cuideo Asist",
        })}
      />
      <Grid container spacing={8}>
        <Grid item xs={12} md={6}>
          <FormikRadioGroup
            name="Direccion_entrega_Cuideo_Asist_distinta_servicio"
            fullWidth
            label={
              <FormattedMessage
                id="CuideoAsistForm.AddressDistinct"
                defaultMessage="Dirección de entrega distinta"
              />
            }
            size="small"
            variant="compact"
            margin="nomargin"
            options={[
              {
                label: formatMessage({
                  id: "CuideoAsistForm.AddressDistinct.Yes",
                  defaultMessage: "Si",
                }),
                value: "Si",
              },
              {
                label: formatMessage({
                  id: "CuideoAsistForm.AddressDistinct.No",
                  defaultMessage: "No",
                }),
                value: "No",
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormikDatePicker
            name="Inicio_de_servicio"
            label={
              <FormattedMessage
                id="ServiceValuationForm.Fecha inicio Cuideo Asist"
                defaultMessage="Fecha inicio Cuideo Asist"
              />
            }
            fullWidth
            margin="nomargin"
            size="small"
            required
          />
        </Grid>
        {formikContext.values
          .Direccion_entrega_Cuideo_Asist_distinta_servicio === "Si" && (
          <Grid item xs={12}>
            <StyledAddressWrapper>
              <Grid container spacing={8} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Grid container spacing={8}>
                    <Grid item xs={12}>
                      <FormikAddressField
                        name="Direccion_entrega_Cuideo_Asist"
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.Address"
                            defaultMessage="Dirección"
                          />
                        }
                        fullWidth
                        margin="nomargin"
                        value={
                          formikContext.values.Direccion_entrega_Cuideo_Asist
                        }
                        handleChange={handleAddressChange}
                        handleSelect={handleAddressSelect}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormikTextField
                        name="Codigo_postal_entrega_Cuideo_Asist"
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.PostalCode"
                            defaultMessage="Código Postal"
                          />
                        }
                        fullWidth
                        margin="nomargin"
                        onChange={handlePostalCodeChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormikSelect
                        name="Ciudad_entrega_Cuideo_Asist"
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.City"
                            defaultMessage="Ciudad"
                          />
                        }
                        required
                        options={cityOptions}
                        fullWidth
                        placeholder={formatMessage({
                          id: "Select...",
                          defaultMessage: "Selecciona...",
                        })}
                        variant="compact"
                        size="small"
                        margin="nomargin"
                        disabled={cityOptions.length < 2}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormikSelect
                        name="Region_entrega_Cuideo_Asist"
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.Region"
                            defaultMessage="Región"
                          />
                        }
                        required
                        options={regionOptions}
                        fullWidth
                        placeholder={formatMessage({
                          id: "Select...",
                          defaultMessage: "Selecciona...",
                        })}
                        variant="compact"
                        size="small"
                        margin="nomargin"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormikSelect
                        name="Area_entrega_Cuideo_Asist"
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.Area"
                            defaultMessage="Área"
                          />
                        }
                        required
                        options={areaOptions}
                        fullWidth
                        placeholder={formatMessage({
                          id: "Select...",
                          defaultMessage: "Selecciona...",
                        })}
                        variant="compact"
                        size="small"
                        margin="nomargin"
                        disabled
                      />
                    </Grid>
                    {formikContext.values.Latitud_entrega_Cuideo_Asist && (
                      <Grid item xs={12} md={6}>
                        <FormikTextField
                          name="Latitud_entrega_Cuideo_Asist"
                          label={
                            <FormattedMessage
                              id="ServiceValuationForm.Latitude"
                              defaultMessage="Latitud"
                            />
                          }
                          fullWidth
                          margin="nomargin"
                          disabled
                        />
                      </Grid>
                    )}
                    {formikContext.values.Longitud_entrega_Cuideo_Asist && (
                      <Grid item xs={12} md={6}>
                        <FormikTextField
                          name="Longitud_entrega_Cuideo_Asist"
                          label={
                            <FormattedMessage
                              id="ServiceValuationForm.Longitude"
                              defaultMessage="Longitud"
                            />
                          }
                          fullWidth
                          margin="nomargin"
                          disabled
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6} style={{ minHeight: "22rem" }}>
                  {formikContext.values.Latitud_entrega_Cuideo_Asist &&
                    formikContext.values.Longitud_entrega_Cuideo_Asist && (
                      <AddressMap
                        center={
                          formikContext.values.Latitud_entrega_Cuideo_Asist &&
                          formikContext.values.Longitud_entrega_Cuideo_Asist
                            ? {
                                lat: formikContext.values
                                  .Latitud_entrega_Cuideo_Asist,
                                lng: formikContext.values
                                  .Longitud_entrega_Cuideo_Asist,
                              }
                            : undefined
                        }
                        zoom={
                          formikContext.values.Latitud_entrega_Cuideo_Asist &&
                          formikContext.values.Longitud_entrega_Cuideo_Asist
                            ? 12
                            : 6
                        }
                        formatted_address={
                          formikContext.values
                            .formatted_address_entrega_Cuideo_Asist
                        }
                      />
                    )}
                </Grid>
              </Grid>
            </StyledAddressWrapper>
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          <FormikTextField
            name="Otras_notas_valoracion"
            label={
              <FormattedMessage
                id="ServiceValuationForm.OtherRequeriments"
                defaultMessage="Otros requisitos (se envía al Cliente)"
              />
            }
            fullWidth
            multiline
            maxRows="8"
            minRows="3"
            margin="nomargin"
          />
        </Grid>
      </Grid>
    </FormBox>
  );
};

export default CuideoAsistForm;
